import type { ComponentProps } from "react";

import { cn } from "../lib/cn";

/** BCP-47 tags used for `Intl` formatting per UI locale. */
const INTL_LOCALE: Readonly<Record<string, string>> = {
  en: "en-US",
  ja: "ja-JP",
  th: "th-TH",
  "zh-Hant": "zh-Hant-TW",
  "zh-Hans": "zh-Hans-CN",
};

/**
 * Format an integer JPY amount. Throws on non-integers: money is never a float (ADR 0003) and the
 * client only ever displays amounts computed by the server. The sign is the half-width `¥` in
 * every language (design/03 §8.6, the e-mails and receipts) — Intl's ja-JP writes `￥` (T13-21).
 */
export function formatJpy(amount: number, locale = "en"): string {
  if (!Number.isInteger(amount)) {
    throw new TypeError(`JPY amount must be an integer, got ${String(amount)}`);
  }
  return new Intl.NumberFormat(INTL_LOCALE[locale] ?? locale, {
    style: "currency",
    currency: "JPY",
    currencyDisplay: "narrowSymbol",
    maximumFractionDigits: 0,
  })
    .format(amount)
    .replace("￥", "¥");
}

export interface PriceProps extends Omit<ComponentProps<"span">, "children"> {
  /** Integer JPY (税込 for domestic listings). */
  amount: number;
  locale?: string;
  /** Translated "税込" / "tax incl." marker (総額表示); rendered small after the amount. */
  taxLabel?: string;
  /** Optional compare-at price (must have evidence per 景品表示法 — the server decides). */
  compareAt?: number;
}

export function Price({
  amount,
  locale = "en",
  taxLabel,
  compareAt,
  className,
  ...props
}: PriceProps) {
  return (
    <span
      data-slot="price"
      className={cn("inline-flex items-baseline gap-1 tabular-nums", className)}
      {...props}
    >
      <span className="font-semibold">{formatJpy(amount, locale)}</span>
      {taxLabel ? <span className="text-xs text-muted-foreground">{taxLabel}</span> : null}
      {compareAt !== undefined && compareAt > amount ? (
        <s className="text-xs text-muted-foreground">{formatJpy(compareAt, locale)}</s>
      ) : null}
    </span>
  );
}
